import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { clx } from "../../../utils/Helpers";
import { UIModal, UITextarea } from "../../ui";

import "./RejectReasonModal.scss";

/**
 * Shared “reject with reason” dialog (registration reject, profile edit reject, etc.).
 */
const RejectReasonModal = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  cancelLabel,
  minReasonLength,
  requiredErrorMessage,
  minLengthErrorMessage,
  submitting,
  modalClassName,
  reasonInputId,
}) => {
  const [reason, setReason] = useState("");
  const [reasonBlurred, setReasonBlurred] = useState(false);
  const [confirmAttempted, setConfirmAttempted] = useState(false);
  const [internalSubmitting, setInternalSubmitting] = useState(false);

  const busy = submitting || internalSubmitting;

  useEffect(() => {
    if (!open) {
      setReason("");
      setReasonBlurred(false);
      setConfirmAttempted(false);
      setInternalSubmitting(false);
    }
  }, [open]);

  const trimmedReason = reason.trim();

  const getReasonError = () => {
    if (!trimmedReason) {
      return requiredErrorMessage;
    }
    if (minReasonLength > 1 && trimmedReason.length < minReasonLength) {
      return minLengthErrorMessage;
    }
    return null;
  };

  const reasonErrorMsg = getReasonError();
  const isReasonValid = reasonErrorMsg === null;
  const showReasonError =
    reasonErrorMsg &&
    (reasonBlurred ||
      confirmAttempted ||
      (trimmedReason.length > 0 &&
        minReasonLength > 1 &&
        trimmedReason.length < minReasonLength));

  const handleRequestClose = () => {
    if (busy) return;
    onClose();
  };

  const handleConfirm = async () => {
    setConfirmAttempted(true);
    if (!isReasonValid) return;

    setInternalSubmitting(true);
    try {
      await Promise.resolve(onConfirm(trimmedReason));
      onClose();
    } catch {
      // Caller / interceptors show errors; keep dialog open
    } finally {
      setInternalSubmitting(false);
    }
  };

  return (
    <UIModal
      open={open}
      onClose={busy ? undefined : handleRequestClose}
      onCancel={handleRequestClose}
      onConfirm={handleConfirm}
      type="destructive"
      title={title}
      description={description}
      cancelLabel={cancelLabel}
      confirmLabel={confirmLabel}
      cancelButtonProps={{ disabled: busy }}
      confirmButtonProps={{ disabled: busy || !isReasonValid }}
      PaperProps={{
        className: clx("reject-reason-modal", modalClassName),
      }}
      aria-labelledby="reject-reason-modal-title"
      disableEscapeKeyDown={busy}
    >
      <UITextarea
        id={reasonInputId}
        name={reasonInputId}
        label={reasonLabel}
        required
        value={reason}
        onValueChange={setReason}
        onBlur={() => setReasonBlurred(true)}
        placeholder={reasonPlaceholder}
        disabled={busy}
        error={Boolean(showReasonError)}
        helperText={showReasonError ? reasonErrorMsg : undefined}
      />
    </UIModal>
  );
};

RejectReasonModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Receives trimmed reason; may return a Promise (e.g. API call). */
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  /** String or React node (e.g. rich description with bold name). */
  description: PropTypes.node.isRequired,
  reasonLabel: PropTypes.string.isRequired,
  reasonPlaceholder: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string.isRequired,
  minReasonLength: PropTypes.number,
  requiredErrorMessage: PropTypes.string.isRequired,
  minLengthErrorMessage: PropTypes.string,
  submitting: PropTypes.bool,
  modalClassName: PropTypes.string,
  reasonInputId: PropTypes.string,
};

RejectReasonModal.defaultProps = {
  minReasonLength: 1,
  minLengthErrorMessage: "",
  submitting: false,
  modalClassName: "",
  reasonInputId: "reject-reason-input",
};

export default RejectReasonModal;
